/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 * Title elements for WizardMultiSearch
 *
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.22.0
 */

import { Fragment } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { IModalRenderProps } from '@/main/modules/modal/types/modalTypes';
import { Button } from '@components/common/Button/Button';
import { API_HOSTNAME, WIZARD, WIZARD_ACTION } from '@modules/app/Api';
import ExportMessage from '@modules/exports/components/ExportMessage';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { ICommonTranslation } from '@modules/internationalization/types';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { IWizardState } from '@modules/wizard/wizardReducer';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { IRootReducer } from '@reducers/rootReducer';
import { openExternalRoute } from '@routes/constants/externalRoutes';
import { stringFormat } from '@utils/stringFormat';

import { EWizardMultiSearchSteps } from '../constant';
import { makeSelectWizardMultiSearchStepConfirm } from '../selectors';
import { IWizardMultiSearchStepConfirm } from '../types';

interface IWizardMultiSearchImportElementsProps {
    stepName: string;
    btnExportErrorTitle: string;
}

type IWizardMultiSearchTitleElementsProps = IWizardMultiSearchImportElementsProps & IConnectedProps & IDispatchProps;

const WizardMultiSearchTitleElements = ({
    btnExportErrorTitle,
    stepName,
    stepData,
    openModal,
    translation,
}: IWizardMultiSearchTitleElementsProps) => {

    const handleExportError = () => {
        openModal({
            render: (p: IModalRenderProps) => (
                <ExportMessage
                    handleClose={p.onCloseModal}
                    handleSubmit={() => handleSubmit(p.onCloseModal)}
                    translation={translation}
                />
            ),
        });
    };

    const handleSubmit = (onCloseModal: () => void) => {
        const url =
            API_HOSTNAME +
            stringFormat(WIZARD, EWizardName.MULTI_SEARCH) +
            stringFormat(WIZARD_ACTION, 'export-not-found');

        openExternalRoute(url, '_blank');

        onCloseModal();
    };

    return (
        <Fragment>
            {stepName === EWizardMultiSearchSteps.Confirm && !!stepData.notFoundMsisdnsCount && (
                <Button
                    className="button__button-content_export"
                    dataName="wizard-multi-search-list-not-found-download"
                    kind="small"
                    onClick={handleExportError}
                >
                    {btnExportErrorTitle}
                </Button>
            )}
        </Fragment>
    );
};

interface IConnectedProps extends Pick<IWizardState, 'stepName'> {
    translation: ICommonTranslation;
    stepData: IWizardMultiSearchStepConfirm;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    stepName: makeSelectWizardStepName(),
    translation: makeSelectTranslationCommon(),
    stepData: makeSelectWizardMultiSearchStepConfirm(),
});

interface IDispatchProps {
    openModal: (payload: IModalOpenPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    openModal: (payload) => dispatch(modalOpen(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardMultiSearchTitleElements);
